import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Check, X, MapPin, MessageCircle, Users, Minus, Plus } from "lucide-react";
import { TOURS, CONTACT, heroSrcSet } from "../mock";
import { DateField } from "../components/site/BookingWidget";
import { format } from "date-fns";
import { api } from "../lib/api";

const MapEmbed = ({ center }) => {
  const [lat, lon] = center;
  const d = 0.08;
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${lon - d}%2C${lat - d}%2C${lon + d}%2C${lat + d}&layer=mapnik&marker=${lat}%2C${lon}`;
  return <iframe title="map" src={src} className="w-full h-72 rounded-2xl border border-[#183C35]/10" loading="lazy" />;
};

const TourDetail = () => {
  const { slug } = useParams();
  const t = TOURS.find((x) => x.slug === slug) || TOURS[0];
  const [date, setDate] = useState();
  const [guests, setGuests] = useState(2);

  const waLink = () => {
    const msg = [
      "Hello Aloha Corfu Transfers, I would like a price for a private tour:",
      `Tour: ${t.title} (${t.hours} hours)`,
      date ? `Date: ${format(date, "EEE, dd MMM yyyy")}` : "Date: flexible",
      `Guests: ${guests}`,
    ].join("\n");
    return `https://wa.me/${CONTACT.phoneRaw}?text=${encodeURIComponent(msg)}`;
  };
  const logWa = () => {
    api.post("/enquiries/tour", { tour: t.title, hours: t.hours, date: date ? format(date, "yyyy-MM-dd") : null, guests }).catch(() => {});
  };

  return (
    <div data-testid="tour-detail-page">
      <section className="relative h-[240px] sm:h-[340px] overflow-hidden">
        <img src={t.image} srcSet={heroSrcSet(t.image)} sizes="100vw" alt={`${t.title} – Aloha Corfu tours, private island tour in Corfu`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#183C35]/50" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 flex flex-col justify-end pb-6 sm:pb-10 fade-up">
          <Link to="/tours" className="flex items-center gap-2 text-[15px] text-[#F7F3EA]/85 hover:text-white transition-colors" data-testid="back-to-tours"><ArrowLeft className="w-4 h-4" />All tours</Link>
          <p className="flex items-center gap-2 mt-3 text-[13px] tracking-[0.15em] uppercase font-semibold text-[#F7F3EA]/85"><Clock className="w-4 h-4" />{t.hours} hours</p>
          <h1 className="font-serif-d font-bold text-[28px] sm:text-[54px] text-[#F7F3EA] mt-1 sm:mt-2 leading-tight">{t.title}</h1>
        </div>
      </section>
      <section className="py-6 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1fr_384px] gap-6 lg:gap-12">
          <div>
            <p className="text-[15.5px] sm:text-[19px] leading-relaxed text-[#202522]/80">{t.desc}</p>
            <h2 className="font-serif-d text-[22px] sm:text-[26px] text-[#183C35] mt-6 sm:mt-10">Itinerary</h2>
            <ol className="mt-5 space-y-3.5">
              {t.itinerary.map((s, i) => (
                <li key={s} className="flex items-center gap-3 text-[15px] sm:text-[17px]"><span className="w-6 h-6 rounded-full bg-[#183C35] text-[#F7F3EA] text-[12px] font-semibold flex items-center justify-center">{i + 1}</span>{s}</li>
              ))}
            </ol>
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mt-6 sm:mt-10">
              <div className="bg-white rounded-2xl border border-[#183C35]/10 p-6">
                <h3 className="font-serif-d text-[20px] text-[#183C35]">What's included</h3>
                <ul className="mt-4 space-y-2.5">{["Private vehicle & driver", "Hotel pickup & drop-off", "Fuel & tolls", "Bottled water"].map((x) => <li key={x} className="flex gap-2.5 text-[15px]"><Check className="w-4 h-4 mt-0.5 text-[#3E7F8E]" />{x}</li>)}</ul>
              </div>
              <div className="bg-white rounded-2xl border border-[#183C35]/10 p-6">
                <h3 className="font-serif-d text-[20px] text-[#183C35]">Not included</h3>
                <ul className="mt-4 space-y-2.5">{["Entrance tickets", "Lunch & drinks", "Gratuities"].map((x) => <li key={x} className="flex gap-2.5 text-[15px]"><X className="w-4 h-4 mt-0.5 text-[#B42318]/70" />{x}</li>)}</ul>
              </div>
            </div>
            <div className="mt-6 sm:mt-10"><MapEmbed center={t.center} /></div>
          </div>
          <aside className="order-first lg:order-none">
            <div className="lg:sticky lg:top-24 bg-white rounded-2xl border border-[#183C35]/10 p-4 sm:p-6 shadow-sm">
              <p className="font-serif-d text-[21px] sm:text-[26px] font-semibold text-[#183C35]">Price on request</p>
              <p className="text-[15px] text-[#202522]/65 mt-1">per private tour · {t.hours} hours</p>
              <div className="mt-4 sm:mt-6 grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] tracking-[0.08em] uppercase font-medium text-[#3E7F8E] mb-2">Date</label>
                  <DateField value={date} onChange={setDate} testid="tour-date" />
                </div>
                <div>
                  <label className="block text-[12px] tracking-[0.08em] uppercase font-medium text-[#3E7F8E] mb-2">Guests</label>
                  <div className="flex items-center justify-between h-[46px] rounded-xl border border-[#183C35]/15 bg-white px-2.5">
                    <Users className="w-4 h-4 text-[#3E7F8E]" />
                    <button type="button" onClick={() => setGuests(Math.max(1, guests - 1))} className="w-10 h-10 sm:w-7 sm:h-7 rounded-full bg-[#F7F3EA] text-[#183C35] flex items-center justify-center hover:bg-[#EDE4D2] transition-colors" data-testid="tour-guests-minus"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="text-[15px] font-medium" data-testid="tour-guests-value">{guests}</span>
                    <button type="button" onClick={() => setGuests(Math.min(16, guests + 1))} className="w-10 h-10 sm:w-7 sm:h-7 rounded-full bg-[#F7F3EA] text-[#183C35] flex items-center justify-center hover:bg-[#EDE4D2] transition-colors" data-testid="tour-guests-plus"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
              <a href={waLink()} target="_blank" rel="noreferrer" onClick={logWa} data-testid="tour-whatsapp-price"
                className="mt-5 w-full h-[52px] rounded-xl bg-[#25D366] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#1FBF5B] transition-colors duration-200">
                <MessageCircle className="w-4 h-4" />Get price on WhatsApp
              </a>
              <p className="mt-3 text-[12.5px] text-[#202522]/60">Opens WhatsApp with the tour, date and guests ready to send. We usually reply within minutes.</p>
              <p className="flex items-center gap-2 mt-5 text-[13px] text-[#202522]/70"><MapPin className="w-3.5 h-3.5 text-[#3E7F8E]" />Hotel/villa pickup included anywhere in Corfu.</p>
            </div>
          </aside>
        </div>
      </section>

    </div>
  );
};

export default TourDetail;
